<template>
  <div class="navigation">
    <div class="main-panel">
      <div class="navigation__links">
        <IconButton
          v-for="item in navigation.items"
          :key="item.slug"
          form="transparent"
          :class="getLinkClass(item)"
          width="full"
          :icon="hasChildLinks(item) ? 'chevron-right' : ''"
          icon-position="right"
          text-align="left"
          :tag="hasChildLinks(item) ? 'button' : 'a'"
          :href="item.url"
          @click="
            handleNavigationClick(item);
            hasChildLinks(item) && openMegaNav(item.slug);
          "
        >
          {{ item.title }}
          <span
            v-if="showMembersCredit(item)"
            class="navigation__members-credit"
          >
            ( {{ bonus }} Credit on Your Order )
          </span>
          <div class="badges">
            <Badge
              v-for="(badge, index) in item.badges"
              :key="index"
              size="small"
              theme="navigation"
            >
              {{ textWithBonus(badge.text) }}
            </Badge>
          </div>
        </IconButton>
      </div>

      <NavigationBanner
        v-if="navigation.banner"
        :images="navigation.banner.images"
        :text="navigation.banner.text"
        :url="navigation.banner.url"
        :align-image-bottom-desktop="
          navigation.banner.alignImageBottomDesktop === true
        "
      />

      <div class="sub-navigation">
        <Button
          form="underline-invert"
          theme="primary"
          tag="a"
          :href="helpUrl"
          class="sub-navigation__link"
        >
          Help
        </Button>
        <Button
          form="underline-invert"
          theme="primary"
          tag="a"
          href="https://help-gb.ratandboa.com/en-US"
          class="sub-navigation__link"
        >
          FAQs
        </Button>
        <Button
          form="underline-invert"
          theme="primary"
          tag="a"
          :href="helpUrl"
          class="sub-navigation__link"
        >
          Returns
        </Button>
      </div>
    </div>
    <ChildNavigationPanel class="sub-panel" />
  </div>
</template>

<script>
import Button from "@/components/ButtonV2.vue";
import IconButton from "@/components/IconButton.vue";
import ChildNavigationPanel from "@/components/navigation/ChildNavigationPanel.vue";
import Badge from "@/components/BadgeV2.vue";
import { mapActions, mapGetters, mapState } from "vuex";
// import navigationContent from "@/content/navigation.json";
import { getSignUpBonus } from "@/config";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import NavigationBanner from "@/components/navigation/NavigationBanner.vue";
import { navigationClickEvent } from "@/helpers/events";

export default {
  name: "Navigation",
  components: {
    Button,
    IconButton,
    ChildNavigationPanel,
    Badge,
    NavigationBanner,
  },
  props: {
    highlightRed: {
      type: Boolean,
      default: false,
    },
    highlight: {
      type: Boolean,
      default: false,
    },
    italic: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dropdownoptions: [
        {
          value: "gbp",
          label: "GBP (£)",
        },
        {
          value: "eur",
          label: "EUR (€)",
        },
        {
          value: "usd",
          label: "USD ($)",
        },
        {
          value: "aud",
          label: "AUD ($)",
        },
      ],
    };
  },
  computed: {
    ...mapState("app", ["navVersion"]),
    ...mapGetters("app", ["currencyCode"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    navigation() {
      try {
        return require(`@/content/navigationV${this.navVersion}.json`);
      } catch (e) {
        console.error(e);
        return require(`@/content/navigation.json`);
      }
    },
    helpUrl() {
      const helpUrls = {
        gbp: "https://help-gb.ratandboa.com/en-US",
        usd: "https://help-us.ratandboa.com/",
        eur: "https://help-eu.ratandboa.com/",
        aud: "https://help-au.ratandboa.com/",
      };
      return helpUrls[this.currencyCode] || helpUrls.gbp;
    },
    bonus() {
      if (!this.currencyCode) {
        return null;
      }
      const featureToggles = getFeatureToggles();
      const amount = getSignUpBonus(this.currencyCode, featureToggles);
      if (typeof amount === "string" && amount.endsWith("%")) {
        return amount;
      }
      return this.$filters.toCurrency(amount, this.currencyCode, 0);
    },
  },
  methods: {
    ...mapActions("app", ["openMegaNav"]),
    hasChildLinks(item) {
      if (item.mega?.columns.length > 0) {
        return true;
      }
      return false;
    },
    textWithBonus(text) {
      if (!text) {
        return null;
      }

      return text.replace("{{bonus}}", this.bonus);
    },
    getLinkClass(item) {
      const baseClass = "navigation__link";
      const classes = [baseClass];

      if (item.modifier) {
        classes.push(`${baseClass}--${item.modifier}`);
      }

      // Support simple boolean italic flag from JSON or prop
      if (item.italic || this.italic) {
        classes.push(`${baseClass}--italic`);
      }

      // Support red highlight via JSON flags or prop (parity with DesktopMegaNav)
      if (
        item.highlightRed === true ||
        item.highlight === true ||
        this.highlightRed ||
        this.highlight
      ) {
        classes.push(`${baseClass}--highlight`);
      }

      if (item.disabled || this.disabled) {
        classes.push(`${baseClass}--disabled`);
      }

      if (this.isMembersItem(item)) {
        classes.push(`${baseClass}--members`);
      }

      return classes.join(" ");
    },
    isMembersItem(item) {
      return item?.slug === "members" || item?.title === "Members";
    },
    showMembersCredit(item) {
      return this.isMembersItem(item) && !this.isLoggedIn;
    },
    handleNavigationClick(item) {
      console.log("handleNavigationClick", item);
      navigationClickEvent({
        title: item.title,
        url: item.url,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";
@import "@/assets/css/components/navigation";

.navigation {
  position: relative;
  display: flex;
  overflow: hidden;

  .main-panel,
  .sub-panel {
    flex: 1 0 100%;
  }

  &__links {
    @extend .container;
    padding-bottom: 48px;
  }

  &__link {
    padding: 8px 0;
    @include editorial-body(14px);
    text-transform: initial;

    :deep(.text) {
      display: inline-flex;
      width: fit-content;
      max-width: 100%;
    }

    :deep(.icon) {
      font-size: 16px;
    }

    &--highlight {
      color: $color-red;
    }

    &--members {
      color: $color-members-green;
    }

    &--italic {
      font-style: italic;
      // Ensure the inner button text also renders italic
      :deep(.text) {
        font-style: italic;
      }
    }

    &--disabled {
      pointer-events: none;
      opacity: 0.5;
      cursor: not-allowed;
    }
    .badges {
      position: relative;
      top: -5px;
      :deep(.badge) {
        background: none;
        color: $color-red;
      }
    }
  }

  &__members-credit {
    margin-left: 8px;
    white-space: nowrap;
  }
}

.sub-navigation {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  @extend .container;
  padding-top: 48px;
  padding-bottom: 48px;
  position: relative;

  .button {
    @include editorial-body(14px);
    text-transform: initial;
  }

  &::after {
    content: "";
    display: block;
    height: 1px;
    background: $color-grey-light;
    position: absolute;
    top: 0px;
    width: 60px;
  }
}
</style>
